import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/site/Reveal";
import SectionHead from "@/components/site/SectionHead";
import CTASection from "@/components/site/CTASection";
import { useLang } from "@/i18n";

const Checkup = () => {
  const { d } = useLang();
  const p = d.checkup;
  return (
    <div data-testid="checkup-page">
      <section className="bg-paper py-20 lg:py-28" data-testid="checkup-hero">
        <div className="mx-auto grid max-w-7xl items-end gap-12 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-accent"
            >
              <span className="h-px w-10 bg-accent" /> {p.heroLabel}
            </motion.p>
            <h1 className="max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="checkup-headline">
              <MaskedLine delay={0.1}>{p.heroL1}</MaskedLine>
              <MaskedLine delay={0.25}><span className="italic text-accent">{p.heroL2}</span></MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-8 max-w-2xl text-base leading-relaxed text-mutedink sm:text-lg"
            >
              {p.sub}
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="rounded-md border border-ink/12 bg-white p-8 lg:col-span-4 lg:p-10"
            data-testid="checkup-price-card"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink">{p.heroLabel}</p>
            <p className="mt-4 font-serif text-6xl tracking-tight text-ink">
              {p.price} <span className="font-sans text-base font-normal text-mutedink">{p.vat}</span>
            </p>
            <p className="mt-4 border-l-2 border-accent pl-4 text-sm leading-relaxed text-mutedink" data-testid="checkup-price-note">{p.priceNote}</p>
            <Link
              to="/contatti?area=checkup"
              data-testid="checkup-hero-cta"
              className="mt-7 inline-flex w-full items-center justify-center gap-3 rounded-md bg-accent px-7 py-4 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-accent-dark"
            >
              {p.cta} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contatti"
              data-testid="checkup-hero-cta-secondary"
              className="mt-3 inline-flex w-full items-center justify-center gap-3 rounded-md border border-ink px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-ink hover:text-white"
            >
              {p.ctaSecondary}
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="checkup-includes">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.includesOverline} title={p.includesTitle} lead={p.includesLead} />
          <div className="mt-16 grid gap-px overflow-hidden rounded-md bg-ink/10 lg:grid-cols-3">
            {p.groups.map((g, i) => (
              <Reveal key={g.t} delay={i * 0.08} className="bg-white p-8 lg:p-10" data-testid={`checkup-group-${i}`}>
                <p className="font-serif text-4xl text-outline">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="mt-5 font-serif text-2xl text-ink">{g.t}</h3>
                <ul className="mt-6 space-y-3 border-t border-ink/10 pt-6 text-sm">
                  {g.items.map((item) => (
                    <li key={item} className="flex gap-3">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" strokeWidth={2} />
                      <span className="text-ink/85">{item}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32" data-testid="checkup-excludes">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <SectionHead overline={p.excludesOverline} title={p.excludesTitle} />
              <Reveal delay={0.15}>
                <p className="mt-6 border-l-2 border-accent pl-5 font-serif text-xl italic leading-snug text-ink" data-testid="checkup-excludes-note">
                  {p.excludesNote}
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid gap-3 sm:grid-cols-2">
                {p.excludes.map((e, i) => (
                  <Reveal key={e} delay={i * 0.05} className="flex items-start gap-3 rounded-md border border-ink/12 bg-white p-5" data-testid={`checkup-exclude-${i}`}>
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-tortora" strokeWidth={2} />
                    <span className="text-sm leading-relaxed text-ink/80">{e}</span>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-ink/10 bg-white py-24 lg:py-32" data-testid="checkup-call">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <SectionHead overline={p.callOverline} title={p.callTitle} />
              <Reveal delay={0.1}>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-mutedink">{p.callBody}</p>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="rounded-md bg-ink p-8 text-paper lg:p-10" data-testid="checkup-call-card">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-accent-bright">{p.callCardLabel}</p>
                <p className="mt-3 font-serif text-3xl">{p.callCardPrice}</p>
                <p className="mt-3 text-sm leading-relaxed text-white/65">{p.callCardBody}</p>
                <Link
                  to="/contatti"
                  data-testid="checkup-call-cta"
                  className="mt-6 inline-flex items-center gap-3 rounded-md bg-white px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-accent hover:text-white"
                >
                  {p.ctaSecondary} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-paper py-24 lg:py-32" data-testid="checkup-path">
        <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
          <SectionHead overline={p.pathOverline} title={p.pathTitle} />
          <div className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-md bg-ink/10 sm:grid-cols-3 lg:grid-cols-5" data-testid="checkup-path-steps">
            {p.pathSteps.map((s, i) => (
              <Reveal key={s} delay={i * 0.06} className="group bg-paper p-6 transition-colors duration-300 hover:bg-ink" data-testid={`checkup-path-${i}`}>
                <p className="font-serif text-4xl text-outline transition-colors duration-300 group-hover:text-accent group-hover:[-webkit-text-stroke:0]">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-5 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-ink transition-colors duration-300 group-hover:text-paper">
                  {s}
                </h3>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-10">
            <p className="max-w-3xl border-l-2 border-accent pl-5 text-sm font-medium leading-relaxed text-ink" data-testid="checkup-path-note">
              {p.pathNote}
            </p>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
};

export default Checkup;
